import { CAAS_INDEX_SUFFIX, DISABLE_ADS } from "@src/app/constants";
import {
  GA4_REF_CODE_CONTENTBLOCK_F,
  GA4_REF_CODE_CONTENTBLOCK_H,
  GA4_REF_CODE_CONTENTBLOCK_L,
} from "@src/app/utils/ga/ref-code";
import {
  BRIGHTCOVE_CONFIG,
  DEFAULT_CONFIG_GAM,
  LOTAME_CONFIG,
  PREMUTIVE_CONFIG,
} from "@src/pubs/common/config";

import {
  ConfigAboutUs,
  ConfigAffiliateLibrary,
  ConfigAffiliatesDeclaration,
  ConfigAffiliateShopping,
  ConfigAffiliatesProduct,
  ConfigAuthorProfile,
  ConfigBrightcove,
  ConfigCaas,
  ConfigCategory,
  ConfigContentHub,
  ConfigError,
  ConfigExploreMore,
  ConfigGAM,
  ConfigGTM,
  ConfigHome,
  ConfigLatestStory,
  ConfigLotame,
  ConfigNewsletter,
  ConfigPermutive,
  ConfigRss,
  ConfigSearch,
  ConfigSingleStory,
  ConfigSite,
  ConfigSkimlinks,
  ConfigTaboola,
  ConfigTag,
  ConfigTeaser,
  ConfigTrackingCode,
  ConfigVideoLanding,
  ConfigVideoOverlayPortrait,
} from "../types/config";
import {
  peakAffiliateArrowLeft,
  peakAffiliateArrowRight,
} from "./icons/affiliatesArrow";
import logoPath from "./logo.svg?url";
import newsletterBg from "./newsletterbg.png";

export const configSite: ConfigSite = {
  name: "PEAK Singapore",
  address:
    import.meta.env.VITE_PUBLIC_BASE_URL ||
    "https://www.thepeakmagazine.com.sg/", // without trailing slash
  tagline: "Meaningful Reads for Modern Minds",
  description:
    "The Peak is a business-lifestyle publication rooted in meaningfulness, curating stories that inspire leaders and visionaries to lead impactful lives.",
  titleSeparator: "—",
  logoPath,
  pageShare: {
    size: 36,
    fill: "#4A5544",
    media: [
      {
        name: "Facebook",
      },
      {
        name: "LinkedIn",
      },
      {
        name: "Telegram",
      },
      {
        name: "Whatsapp",
      },
      {
        name: "Email",
      },
    ],
  },
  meta: [
    { name: "msapplication-TileColor", content: "#b92427" },
    { name: "theme-color", content: "#ffffff" },
  ],
  links: [
    {
      rel: "icon",
      type: "image/png",
      sizes: "32x32",
      href: "/favicon-32x32.png",
    },
    {
      rel: "icon",
      type: "image/png",
      sizes: "16x16",
      href: "/favicon-16x16.png",
    },
    { rel: "mask-icon", href: "/safari-pinned-tab.svg", color: "#b92427" },
  ],
};

export { configHeader } from "./config/configHeader";

export const configCaas: ConfigCaas = {
  index: `thepeak${CAAS_INDEX_SUFFIX}`,
  tagIdPrefix: "tag:thepeak-keywords@sph.com.sg,2023:",
  cuePublicationName: "thepeak",
};

export const configGTM: ConfigGTM = {
  container: "GTM-MRJP6VP", //Peak
  siteVerification: "_cGEdj2ZMeQhb_OTIAKvlCGbSxaBwct6M6XrDxywcXM",
};

export const configGAM: ConfigGAM = {
  ...DEFAULT_CONFIG_GAM,
  account: "/5908/peak-sg",
  debounce: true,
};

export const configLotame: ConfigLotame = {
  ...LOTAME_CONFIG,
  active: !DISABLE_ADS,
};

export const configPermutive: ConfigPermutive = {
  ...PREMUTIVE_CONFIG,
  active: !DISABLE_ADS,
};

export const configSkimlinks: ConfigSkimlinks = {
  active: !DISABLE_ADS,
  skimlinkId: "157725X1768900",
  geniuslinkId: "394420",
};

export const configBrightcove: ConfigBrightcove = {
  ...BRIGHTCOVE_CONFIG,
};

export const configSingleStory: ConfigSingleStory = {
  byline: {
    defaultCredit: "The Peak Team",
    separator: "\u00a0/\u00a0",
  },
  authorProfile: true,
  configShareArticle: {
    label: "Share this article",
    divider: {
      top: false,
    },
  },
  topDivider: false,
  gallery: {
    paginationSeparator: "/",
  },
  featureStory: {
    bodyMaxWidth: 890,
  },
  displayTags: {
    displayTopTags: true,
    displayBottomTags: true,
    displayTagHeading: false,
  },
};

export const configLatestStory: ConfigLatestStory = {
  latestStoryLimit: 21,
  subAnchorLimit: 3,
  landscapeIndexes: [0, 1, 2],
  mainContentBlockDisplay: "grid",
  mainContentBlockMode: "landscape",
  errorLayoutMode: "landscape",
  portraitImageWidth: 285,
  landscapeImageWidth: 345,
};

export const configCategory: ConfigCategory = {
  pinnedTeasersLimit: 3,
  latestStoriesLimit: 8,
  adPositioning: 3,
  videoGalleryLandscape: {
    mobileNavigation: true,
    display: true,
  },
  dividerVariant: "secondary",
  showVerticalLine: true,
};

export const configContentHub: ConfigContentHub = {
  configAds: {
    showInlineAds: true,
  },
  configMasthead: {
    heading: {
      display: true,
    },
    gradientOverlay: {
      display: true,
    },
  },
  configBodyContent: {
    display: true,
  },
  configShareButton: {
    display: false,
  },
  configEditorsNote: {
    display: false,
  },
  configCallQoute: {
    display: true,
  },
  configFeaturedSection: {
    display: true,
  },
  configDeals: {
    display: true,
    props: {
      heading: "editor's picks",
      noOfItemsInFrame: 4,
    },
  },
  configTopStories: {
    display: true,
    listingPerPage: 6,
  },
  storiesPerPage: {
    topStories: 4,
    totalStories: 10,
  },
  configListingStories: {
    mode: "landscape",
  },
  contenthubBlockF: {
    mode: "landscape",
  },
  layout: {
    stories: {
      elements: [
        { name: "contentBlockB", limit: 4 },
        { name: "adsMidContent" },
      ],
    },
  },
};

export const configTag: ConfigTag = {
  topStoriesLimit: 4,
  listedStoriesLimit: 12,
  itemSeparator: ", ",
  subHeading: "Tag",
  showVerticalLine: true,
};

export const configAmazonAffiliates = {};

export const configTaboola: ConfigTaboola = {
  publisherId: "sphmedia-thepeaksg",
};

export const configSearch: ConfigSearch = {
  loadMoreText: "Loading more results",
};

export const configExploreMore: ConfigExploreMore = {
  heading: "Explore other topics",
};

export const configVideoLanding: ConfigVideoLanding = {};

export const configVideoOverlayPortrait: ConfigVideoOverlayPortrait = {};

export const configHome: ConfigHome = {
  anchorBlock: {
    hideBreadCrumb: false,
    hideVerticalLine: true,
  },
  isHeadingClickable: true,
  headingDivider: false,
  viewMore: { icon: false },
  latestArticle: {
    mobileMode: "landscape",
    landscapeIndexes: [0, 1, 2, 3],
    viewMore: {
      text: "VIEW ALL",
    },
  },
  layoutGrid: {
    elements: [
      {
        name: "anchorBlock",
        dataSource: {
          type: "list",
          key: "anchorblock",
          fallback: "latest",
          limit: 1,
        },
      },
      {
        name: "latestArticle",
        dataSource: {
          type: "list",
          key: "pinned",
          fallback: "latest",
          limit: 4,
        },
      },
      {
        name: "contentBlockJ",
        dataSource: {
          type: "tag",
          key: "topic-0",
          refCode: "contenthub",
          limit: 4,
          min: 4,
        },
        alternateBg: true,
        trendingBlock: false,
      },
      {
        name: "contentBlockF",
        dataSource: {
          type: "section",
          key: "people",
          refCode: "block-c",
          limit: 4,
        },
        alternateBg: false,
      },
      {
        name: "contentBlockK",
        dataSource: {
          type: "section",
          key: "watch-jewellery",
          limit: 3,
          refCode: "block-d",
        },
      },
      { name: "adUnit" },
      {
        name: "contentBlockL",
        dataSource: {
          type: "section",
          key: "lifestyle",
          refCode: "block-b",
          limit: 5,
        },
      },
      {
        name: "contentBlockF",
        dataSource: {
          type: "section",
          key: "influence",
          refCode: "contentblock",
          limit: 4,
        },
      },
      {
        name: "contentBlockJ",
        dataSource: {
          type: "list",
          fallback: "trending",
          key: "trending",
          refCode: "trending",
          limit: 4,
        },
        alternateBg: true,
        trendingBlock: true,
      },
      {
        name: "socialVideoPortrait",
        dataSource: {
          type: "list",
          key: "socialvideos",
        },
      },
      {
        name: "magazineListing",
      },
      {
        name: "newsletter",
      },
    ],
  },
  magazineListing: {
    midDivider: true,
    midDividerText: "OR",
  },
  newsletter: {
    containerImageBg: newsletterBg,
    newsletterTitle:
      "Enjoy meaningful reads on thought leadership and luxury lifestyle for today's leaders and visionaries.",
    newsletterBody: "Sign up for our weekly newsletter.",
  },
  contentHubStories: 4,
  contentBlockF: {
    limit: 4,
    landscapeIndexes: [0, 1, 2, 3],
    layoutTwoColumn: [1, 2, 3],
    anchorBlock: {
      index: 0,
      mediaScreen: "all-screen",
      hideVerticalLine: false,
    },
    landscapeImageWidth: 295,
    twoColumnsLandscapeImageWidth: 300,
  },
  contentBlockL: {
    landscapeIndexes: [0, 1, 2, 3, 4],
    anchorBlock: {
      index: 0,
      mediaScreen: "all-screen",
      hideVerticalLine: false,
    },
    landscapeImageWidth: 295,
  },
  contentBlockK: {
    skipSectionContext: true,
  },
  socialVideoLimit: 3,
  trending: {
    limit: 4,
  },
  trendingStories: {
    publisher: "thepeak",
    window: 7,
  },
};

export const configNewsletter: ConfigNewsletter = {
  image: newsletterBg,
  title:
    "Enjoy meaningful reads on thought leadership and luxury lifestyle for today's leaders and visionaries.",
  body: "Sign up for our weekly newsletter.",
};

export const configTeaser: ConfigTeaser = {
  publishDate: false,
  anchorBlock: {
    mobileCrop: "landscape",
    publishDate: false,
  },
};

export const configTrackingCode: ConfigTrackingCode = {
  home: {
    topic: ["contenthub"],
    trending: "trending",
    blockk: "block-d",
    blockl: "block-b",
    blockf: ["block-c", "contentblock"],
    blockd: "howto",
  },
  category: {
    mainContentBlock: GA4_REF_CODE_CONTENTBLOCK_L,
  },
  exploremore: "exploremore",
  contenthub: {
    topstories: {
      contentBlockB: GA4_REF_CODE_CONTENTBLOCK_F,
    },
    listing: GA4_REF_CODE_CONTENTBLOCK_H,
  },
};

export const configAffiliatesDeclaration: ConfigAffiliatesDeclaration = {
  text: "This article contains affiliate links. If you buy through these links, we may earn a small commission.",
};

export const configAboutUs: ConfigAboutUs = {
  profileBlock: {
    heading: "Meet The Team",
  },
};

export const configError: ConfigError = {
  relatedTitle: "Perhaps what you are looking for is here.",
};

export const configAuthorProfile: ConfigAuthorProfile = {
  sectionHeadingText: "Articles by",
  hideAds: true,
};

export const configAffiliatesProduct: ConfigAffiliatesProduct = {};
export const configAffiliateShopping: ConfigAffiliateShopping = {};

export const configRss: ConfigRss = [];

export const configAffiliateLibrary: ConfigAffiliateLibrary = {
  enable: true,
  arrow: {
    left: peakAffiliateArrowLeft,
    right: peakAffiliateArrowRight,
  },
};
